/**
 * Shared pin-collection logic for collection and article pages.
 *
 * Usage:
 *   cocaPinCollection.init({
 *     gridSelector: '.coca-product-grid',          // required
 *     containerSelector: '.coca-collection__products', // required
 *     loadingSelector: '.coca-collection__pin-loading', // optional, skeleton element
 *     paginationSelector: '.coca-pagination-controller', // optional
 *     collectionHandle: 'my-collection',            // optional
 *     pinCollectionHandle: '所有商品',               // optional, fallback collection for additional
 *     storefrontToken: '',                          // required for variant/additional/doubleup
 *     storefrontApiVersion: '2026-01',
 *     storeDomain: 'my-store.myshopify.com',
 *   });
 */
;(function () {
  'use strict';

  var store = {
    config: null,
    running: false,
    doneKey: '',
  };

  /* ------------------------------------------------------------------ */
  /*  Utilities                                                          */
  /* ------------------------------------------------------------------ */

  function normalizeHandle(value) {
    return String(value || '').trim().toLowerCase();
  }

  function uniqueHandles(values) {
    var seen = {};
    var result = [];
    (values || []).forEach(function (v) {
      var h = normalizeHandle(v);
      if (h && !seen[h]) {
        seen[h] = true;
        result.push(h);
      }
    });
    return result;
  }

  function chunkArray(items, size) {
    var chunks = [];
    for (var i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  }

  function logDuration(label, startTime, extra) {
    var duration = Math.round((performance.now() - startTime) * 100) / 100;
    if (extra && typeof extra === 'object') {
      /* console.log('[coca-pin] ' + label + ' took ' + duration + 'ms', extra); */
    }
  }

  /* ------------------------------------------------------------------ */
  /*  URL parameter parsing                                               */
  /* ------------------------------------------------------------------ */

  function getPinsFromUrl() {
    var params = new URLSearchParams(window.location.search);
    function parseList() {
      var names = Array.prototype.slice.call(arguments);
      for (var i = 0; i < names.length; i++) {
        var rawValue = params.get(names[i]);
        if (!rawValue) continue;
        return rawValue
          .split(',')
          .map(function (s) { return String(s || '').trim(); })
          .filter(Boolean);
      }
      return [];
    }

    var pageValue = parseInt(params.get('page') || '1', 10);
    return {
      page: Number.isNaN(pageValue) ? 1 : pageValue,
      pins: parseList('pins'),
      variants: parseList('variants', 'vaints'),
      additional: Boolean(params.get('additional')),
      doubleup: String(params.get('doubleup') || '').trim() === '1',
    };
  }

  /* ------------------------------------------------------------------ */
  /*  Storefront API                                                      */
  /* ------------------------------------------------------------------ */

  function storefrontRequest(query, variables) {
    var cfg = store.config;
    if (!cfg.storefrontToken) return Promise.resolve(null);

    return fetch('https://' + cfg.storeDomain + '/api/' + cfg.storefrontApiVersion + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.storefrontToken,
      },
      body: JSON.stringify({ query: query, variables: variables || {} }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Storefront API request failed with status ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        if (payload.errors && payload.errors.length) {
          throw new Error(payload.errors.map(function (e) { return e.message; }).join('; '));
        }
        return payload.data || null;
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Variant resolution                                                  */
  /* ------------------------------------------------------------------ */

  function resolveVariantHandles(variantIds) {
    var results = new Map();
    var uniqueIds = uniqueHandles(variantIds);
    if (!uniqueIds.length || !store.config.storefrontToken) return Promise.resolve(results);

    var query = [
      'query cocaResolveVariants($ids: [ID!]!) {',
      '  nodes(ids: $ids) {',
      '    ... on ProductVariant {',
      '      id',
      '      product { handle }',
      '    }',
      '  }',
      '}',
    ].join('\n');

    var chain = Promise.resolve();
    chunkArray(uniqueIds, 50).forEach(function (chunk) {
      chain = chain.then(function () {
        var ids = chunk.map(function (id) { return 'gid://shopify/ProductVariant/' + id; });
        return storefrontRequest(query, { ids: ids });
      }).then(function (data) {
        var nodes = (data && data.nodes) || [];
        nodes.forEach(function (node) {
          if (!node || !node.id || !node.product || !node.product.handle) return;
          var variantId = String(node.id).split('/').pop();
          if (!variantId) return;
          results.set(String(variantId), {
            variantId: String(variantId),
            handle: String(node.product.handle).trim().toLowerCase(),
          });
        });
      });
    });

    return chain.then(function () { return results; });
  }

  /* ------------------------------------------------------------------ */
  /*  Pinned card URL & fetch                                             */
  /* ------------------------------------------------------------------ */

  function buildPinnedCardUrl(handle, variantId) {
    var cfg = store.config;
    var params = new URLSearchParams();
    params.set('view', 'coca-pin-card');
    params.set('show_color_dots', '0');
    if (variantId) params.set('variant', String(variantId));
    if (cfg.collectionHandle) params.set('collection_handle', String(cfg.collectionHandle));
    window.__cocaPinnedCardCacheBustToken = window.__cocaPinnedCardCacheBustToken || (Date.now() + '-' + Math.random().toString(36).slice(2, 10));
    params.set('_coca_pin_cb', window.__cocaPinnedCardCacheBustToken);
    return '/products/' + encodeURIComponent(handle) + '?' + params.toString();
  }

  function fetchPinnedCard(item) {
    var requestUrl = buildPinnedCardUrl(item.handle, item.variantId);
    return fetch(requestUrl, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) return null;
        return response.text();
      })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var element = doc.querySelector('.coca-product-item');

        if (!element) {
          var fallbackWrapper = doc.createElement('div');
          fallbackWrapper.className = 'coca-product-item';
          while (doc.body.firstChild) {
            fallbackWrapper.appendChild(doc.body.firstChild);
          }
          element = fallbackWrapper.childNodes.length ? fallbackWrapper : null;
        }

        if (!element) return null;

        var imported = document.importNode(element, true);
        if (!imported.classList || !imported.classList.contains('coca-product-item')) {
          var wrapper = document.createElement('div');
          wrapper.className = 'coca-product-item';
          wrapper.appendChild(imported);
          imported = wrapper;
        }

        return imported;
      })
      .catch(function () { return null; });
  }

  /* ------------------------------------------------------------------ */
  /*  Grid helpers                                                        */
  /* ------------------------------------------------------------------ */

  function getFirstUnpinnedGridItem(grid) {
    return Array.from(grid.children).find(function (el) {
      return el.classList && el.classList.contains('coca-product-item') && !el.dataset.pinSource;
    }) || null;
  }

  function materializePinnedCard(grid, item) {
    var existingItems = Array.from(grid.querySelectorAll('.coca-product-item[data-handle]')).filter(function (el) {
      return String(el.dataset.handle || '').trim().toLowerCase() === item.handle && !el.dataset.pinSource;
    });

    var existingCard = null;
    if (existingItems.length) {
      existingCard = existingItems[0];
      existingItems.slice(1).forEach(function (el) { el.remove(); });
    }

    return fetchPinnedCard(item).then(function (fetchedCard) {
      var card = fetchedCard || existingCard;
      if (!card) return null;

      if (fetchedCard && existingCard) {
        existingCard.remove();
      }

      card.dataset.handle = item.handle;
      card.dataset.pinSource = item.source;
      card.dataset.pinKey = item.key;
      if (item.variantId) {
        card.dataset.variantId = String(item.variantId);
      } else {
        delete card.dataset.variantId;
      }

      return card;
    });
  }

  /* ------------------------------------------------------------------ */
  /*  DoubleUp logic                                                      */
  /* ------------------------------------------------------------------ */

  function getFirstPageDoubleUpHandles(originalGridHandles, lastPinnedHandle, usedHandles) {
    var normalizedLastPinnedHandle = normalizeHandle(lastPinnedHandle);
    if (!normalizedLastPinnedHandle || !Array.isArray(originalGridHandles) || !originalGridHandles.length) return [];

    var lastPinnedIndex = originalGridHandles.lastIndexOf(normalizedLastPinnedHandle);
    if (lastPinnedIndex === -1) return [];

    var pairStartIndex = lastPinnedIndex % 2 === 0 ? lastPinnedIndex : lastPinnedIndex - 1;
    var handles = [];

    for (var index = pairStartIndex; index < pairStartIndex + 2 && index < originalGridHandles.length; index++) {
      var handle = normalizeHandle(originalGridHandles[index]);
      if (!handle) continue;
      if (usedHandles && usedHandles.has(handle)) continue;
      handles.push(handle);
    }

    return handles;
  }

  /* ------------------------------------------------------------------ */
  /*  Additional / related product resolution                             */
  /* ------------------------------------------------------------------ */

  function resolveProductHandlesFromMetafield(metafield) {
    if (!metafield) return [];

    var handles = [];
    function pushHandle(value) {
      var h = normalizeHandle(value);
      if (h) handles.push(h);
    }

    if (metafield.value) {
      var rawValue = String(metafield.value).trim();
      if (rawValue) {
        rawValue.split(';').forEach(function (part) {
          var trimmed = String(part || '').trim();
          if (!trimmed) return;
          pushHandle(trimmed);
        });
      }
    }

    return uniqueHandles(handles);
  }

  function fetchPinnedRelatedProductHandles(productHandle) {
    var normalizedHandle = normalizeHandle(productHandle);
    if (!normalizedHandle || !store.config.storefrontToken) return Promise.resolve([]);

    window.__cocaPinnedRelatedProductHandlesCache = window.__cocaPinnedRelatedProductHandlesCache || new Map();
    if (window.__cocaPinnedRelatedProductHandlesCache.has(normalizedHandle)) {
      return Promise.resolve(window.__cocaPinnedRelatedProductHandlesCache.get(normalizedHandle));
    }

    var query = [
      'query cocaPinnedRelatedProducts($handle: String!) {',
      '  product(handle: $handle) {',
      '    relatedProducts: metafield(namespace: "custom", key: "similar_products") {',
      '      type',
      '      value',
      '      reference {',
      '        ... on Product { handle }',
      '      }',
      '      references(first: 20) {',
      '        nodes {',
      '          ... on Product { handle }',
      '        }',
      '      }',
      '    }',
      '  }',
      '}',
    ].join('\n');

    return storefrontRequest(query, { handle: normalizedHandle }).then(function (data) {
      var handles = resolveProductHandlesFromMetafield(((data || {}).product || {}).relatedProducts);
      window.__cocaPinnedRelatedProductHandlesCache.set(normalizedHandle, handles);
      return handles;
    });
  }

  function pickRandomUnusedHandle(candidateHandles, usedHandles) {
    var candidates = uniqueHandles(candidateHandles).filter(function (handle) {
      return handle && !usedHandles.has(handle);
    });

    if (!candidates.length) return '';
    var randomIndex = Math.floor(Math.random() * candidates.length);
    return candidates[randomIndex] || '';
  }

  function fetchCollectionProductHandles(collectionHandle) {
    var normalizedCollectionHandle = normalizeHandle(collectionHandle);
    if (!normalizedCollectionHandle || !store.config.storefrontToken) return Promise.resolve([]);

    window.__cocaCollectionProductHandlesCache = window.__cocaCollectionProductHandlesCache || new Map();
    if (window.__cocaCollectionProductHandlesCache.has(normalizedCollectionHandle)) {
      return Promise.resolve(window.__cocaCollectionProductHandlesCache.get(normalizedCollectionHandle));
    }

    var query = [
      'query cocaCollectionProducts($handle: String!) {',
      '  collection(handle: $handle) {',
      '    products(first: 100, sortKey: CREATED, reverse: true) {',
      '      nodes { handle }',
      '    }',
      '  }',
      '}',
    ].join('\n');

    return storefrontRequest(query, { handle: normalizedCollectionHandle }).then(function (data) {
      var handles = uniqueHandles(
        (((data || {}).collection || {}).products || {}).nodes
          ? data.collection.products.nodes.map(function (node) { return node && node.handle; })
          : []
      );
      window.__cocaCollectionProductHandlesCache.set(normalizedCollectionHandle, handles);
      return handles;
    });
  }

  function getAdditionalHandle(usedHandles, lastPinnedHandle) {
    return fetchPinnedRelatedProductHandles(lastPinnedHandle).then(function (relatedHandles) {
      var relatedCandidate = uniqueHandles(relatedHandles).find(function (handle) {
        return handle && !usedHandles.has(handle);
      });
      if (relatedCandidate) return relatedCandidate;

      if (!store.config.storefrontToken || !store.config.pinCollectionHandle) return '';
      return fetchCollectionProductHandles(store.config.pinCollectionHandle).then(function (productHandles) {
        return pickRandomUnusedHandle(productHandles, usedHandles);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Main insert logic                                                   */
  /* ------------------------------------------------------------------ */

  function insertPinnedProducts() {
    var cfg = store.config;
    var container = document.querySelector(cfg.containerSelector);
    if (!container) return;

    var grid = container.querySelector(cfg.gridSelector);
    if (!grid) return;

    var config = getPinsFromUrl();

    if (config.page !== 1 || (!config.pins.length && !config.variants.length && !config.additional && !config.doubleup)) {
      setPinLoadingState(container, false);
      return;
    }

    var runKey = window.location.pathname + window.location.search + cfg.containerSelector;
    if (store.doneKey === runKey || store.running) return;
    store.running = true;
    setPinLoadingState(container, true);

    var originalGridHandles = Array.from(grid.querySelectorAll('.coca-product-item[data-handle]'))
      .map(function (el) { return normalizeHandle(el.dataset.handle); })
      .filter(Boolean);

    var usedHandles = new Set();
    var primaryItems = [];
    var seenPinHandles = {};
    var seenVariantIds = {};

    config.pins.forEach(function (handleValue) {
      var handle = normalizeHandle(handleValue);
      if (!handle) return;
      if (seenPinHandles[handle]) return;
      seenPinHandles[handle] = true;
      usedHandles.add(handle);
      primaryItems.push({
        key: handle,
        handle: handle,
        variantId: '',
        source: 'pin',
      });
    });

    resolveVariantHandles(config.variants)
      .then(function (variantMap) {
        config.variants.forEach(function (variantIdValue) {
          var variantId = String(variantIdValue || '').trim();
          var resolved = variantMap.get(variantId);
          if (!variantId || !resolved || !resolved.handle) return;
          if (seenVariantIds[variantId]) return;
          seenVariantIds[variantId] = true;
          usedHandles.add(resolved.handle);
          primaryItems.push({
            key: variantId,
            handle: resolved.handle,
            variantId: variantId,
            source: 'variant',
          });
        });

        /* --- Insert primary pinned items --- */
        var insertedPinnedItems = [];
        var chain = Promise.resolve();

        primaryItems.forEach(function (item) {
          chain = chain.then(function () {
            return materializePinnedCard(grid, item).then(function (card) {
              if (!card) return;
              var currentAnchor = getFirstUnpinnedGridItem(grid);
              if (currentAnchor) {
                grid.insertBefore(card, currentAnchor);
              } else {
                grid.appendChild(card);
              }
              insertedPinnedItems.push(item);
            });
          });
        });

        /* --- DoubleUp --- */
        chain = chain.then(function () {
          if (!config.doubleup) return;

          var lastPinnedItem = insertedPinnedItems[insertedPinnedItems.length - 1] || primaryItems[primaryItems.length - 1] || null;
          var normalizedLastPinnedHandle = normalizeHandle(lastPinnedItem && lastPinnedItem.handle);
          var lastPinnedIndex = normalizedLastPinnedHandle ? originalGridHandles.lastIndexOf(normalizedLastPinnedHandle) : -1;
          var doubleUpHandles = getFirstPageDoubleUpHandles(
            originalGridHandles,
            lastPinnedItem && lastPinnedItem.handle,
            usedHandles
          );

          var resolveChain = Promise.resolve();

          if (!doubleUpHandles.length) {
            resolveChain = resolveChain.then(function () {
              return getAdditionalHandle(usedHandles, lastPinnedItem && lastPinnedItem.handle);
            }).then(function (relatedHandle) {
              if (relatedHandle) doubleUpHandles = [relatedHandle];
            });
          }

          return resolveChain.then(function () {
            var insertChain = Promise.resolve();
            doubleUpHandles.forEach(function (doubleUpHandle) {
              insertChain = insertChain.then(function () {
                return materializePinnedCard(grid, {
                  key: 'doubleup:' + doubleUpHandle,
                  handle: doubleUpHandle,
                  variantId: '',
                  source: 'doubleup',
                }).then(function (card) {
                  if (!card) return;

                  var doubleUpIndex = originalGridHandles.lastIndexOf(doubleUpHandle);
                  var shouldInsertBeforeLastPinned = lastPinnedIndex !== -1
                    && doubleUpIndex !== -1
                    && doubleUpIndex < lastPinnedIndex;
                  var lastPinnedElement = shouldInsertBeforeLastPinned
                    ? Array.from(grid.children).find(function (el) {
                        return el.classList
                          && el.classList.contains('coca-product-item')
                          && el.dataset.pinSource
                          && normalizeHandle(el.dataset.handle) === normalizedLastPinnedHandle;
                      }) || null
                    : null;
                  var currentAnchor = lastPinnedElement || getFirstUnpinnedGridItem(grid);
                  if (currentAnchor) {
                    grid.insertBefore(card, currentAnchor);
                  } else {
                    grid.appendChild(card);
                  }
                  usedHandles.add(doubleUpHandle);
                });
              });
            });
            return insertChain;
          });
        });

        /* --- Additional --- */
        chain = chain.then(function () {
          if (!config.additional) return;

          var lastPinnedItem = insertedPinnedItems[insertedPinnedItems.length - 1] || primaryItems[primaryItems.length - 1] || null;
          return getAdditionalHandle(usedHandles, lastPinnedItem && lastPinnedItem.handle).then(function (relatedHandle) {
            if (!relatedHandle) return;
            return materializePinnedCard(grid, {
              key: relatedHandle,
              handle: relatedHandle,
              variantId: '',
              source: 'additional',
            }).then(function (card) {
              if (!card) return;
              var currentAnchor = getFirstUnpinnedGridItem(grid);
              if (currentAnchor) {
                grid.insertBefore(card, currentAnchor);
              } else {
                grid.appendChild(card);
              }
            });
          });
        });

        /* --- Done --- */
        chain
          .then(function () {
            store.doneKey = runKey;
          })
          .catch(function (error) {
            console.error('Failed to insert pinned products:', error);
          })
          .then(function () {
            setPinLoadingState(container, false);
            store.running = false;
          });
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Loading state                                                       */
  /* ------------------------------------------------------------------ */

  function setPinLoadingState(container, isLoading) {
    container.dataset.pinLoading = isLoading ? 'true' : 'false';
    container.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    var cfg = store.config;
    if (cfg.loadingSelector) {
      var loadingEl = container.querySelector(cfg.loadingSelector);
      if (loadingEl) loadingEl.hidden = !isLoading;
    }
    var gridEl = container.querySelector(cfg.gridSelector);
    if (gridEl) gridEl.hidden = isLoading;
    if (cfg.paginationSelector) {
      var paginationEl = container.querySelector(cfg.paginationSelector);
      if (paginationEl) paginationEl.hidden = isLoading;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                          */
  /* ------------------------------------------------------------------ */

  function init(config) {
    if (!config || !config.gridSelector || !config.containerSelector) {
      console.warn('[coca-pin-collection] gridSelector and containerSelector are required.');
      return;
    }

    store.config = {
      gridSelector: config.gridSelector,
      containerSelector: config.containerSelector,
      loadingSelector: config.loadingSelector || '',
      paginationSelector: config.paginationSelector || '',
      collectionHandle: config.collectionHandle || '',
      pinCollectionHandle: config.pinCollectionHandle || '',
      storefrontToken: config.storefrontToken || '',
      storefrontApiVersion: config.storefrontApiVersion || '2026-01',
      storeDomain: config.storeDomain || '',
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', insertPinnedProducts, { once: true });
    } else {
      insertPinnedProducts();
    }
  }

  window.cocaPinCollection = { init: init };
})();
c